import { useState } from 'react';
import JSZip from 'jszip';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Copy, Plus, RotateCcw, CheckCircle, ExternalLink, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/SharedAuthContext';
import { trpc } from '@/lib/trpc';
import { formatRelativeDate } from '@/lib/skills-store';
import { canRestoreVersion } from '@/lib/skills-permissions';

async function downloadVersion(skillName: string, description: string, prompt: string, versionNum: number) {
  const nameSlug = skillName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/, '');
  const descIndented = description.split('\n').map((l) => `  ${l}`).join('\n');
  const skillMd = `---\nname: ${nameSlug}\ndescription: >\n${descIndented}\n---\n\n${prompt}`;
  const zip = new JSZip();
  zip.file('SKILL.md', skillMd);
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nameSlug}-v${versionNum}.skill`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function SkillVersionHistoryPage() {
  const navigate = useNavigate();
  const { skillId } = useParams<{ skillId: string }>();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const { t } = useTranslation();

  const { data: skill, isLoading } = trpc.skills.getById.useQuery(
    { skillId: skillId! },
    { enabled: !!skillId }
  );

  const restoreVersionMutation = trpc.skills.restoreVersion.useMutation({
    onSuccess: () => {
      toast.success(t('skills.history.restored_toast'));
      utils.skills.getAll.invalidate();
      if (skillId) utils.skills.getById.invalidate({ skillId });
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const currentUser = user
    ? { email: user.email, isAdmin: (user.roles ?? []).includes('admin'), roles: user.roles ?? (user.role ? [user.role] : []) }
    : null;

  // Must be before early returns — Rules of Hooks
  const { data: coverImageData } = trpc.skills.getImageUrl.useQuery(
    { s3Key: skill?.coverImageKey ?? '' },
    { enabled: !!skill?.coverImageKey, staleTime: 55 * 60 * 1000 }
  );

  // Arriba de los returns tempranos: con isLoading=true el primer render salía
  // sin llamar este hook, y cuando la query cargaba lo llamaba — React tira
  // "Rendered more hooks than during the previous render" y la página crashea.
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="p-6 text-center space-y-4">
        <p className="text-muted-foreground">{t('skills.history.skill_no_encontrada')}</p>
        <Button variant="outline" onClick={() => navigate('/skills-agentes')}>
          {t('skills.history.back')}
        </Button>
      </div>
    );
  }

  const sortedVersions = [...skill.versions].sort((a, b) => b.version - a.version);
  const currentVersionData = skill.versions.find((v) => v.isCurrent);
  const canRestore = canRestoreVersion(currentUser, skill);
  const hasContext = skill.coverImageKey || (skill.contextLinks && skill.contextLinks.length > 0) || skill.useCaseText;

  const handleCopyPrompt = async () => {
    const prompt = currentVersionData?.prompt ?? '';
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success(t('skills.card.prompt_copied'));
    } catch {
      toast.error(t('skills.history.no_se_pudo_copiar_el_prompt'));
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-muted-foreground"
          onClick={() => navigate('/skills-agentes')}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('skills.history.back')}
        </Button>
        <h2 className="text-2xl font-bold tracking-tight">
          {skill.name} {t('skills.history.title_suffix')}
        </h2>
        <p className="text-muted-foreground mt-1">
          {t('skills.history.team_prefix')} {skill.team} · {skill.versions.length}{' '}
          {t('skills.history.subtitle')}
        </p>
      </div>

      {/* Version timeline */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t('skills.history.versions_section')}
        </h3>
        {sortedVersions.map((version) => (
          <Card key={version.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant={version.isCurrent ? 'default' : 'outline'}
                  className="font-semibold"
                >
                  v{version.version}
                </Badge>
                {version.isCurrent && (
                  <Badge className="bg-green-500 text-white gap-1">
                    <CheckCircle className="h-3 w-3" />
                    {t('skills.history.current_badge')}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatRelativeDate(version.createdAt)}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadVersion(skill.name, skill.description, version.prompt, version.version)}
                >
                  <Download className="h-3 w-3 mr-1" />
                  {t('skills.history.download_btn')} v{version.version}
                </Button>
                {!version.isCurrent && canRestore && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={restoreVersionMutation.isPending}
                    onClick={() => restoreVersionMutation.mutate({ skillId: skill.id, versionId: version.id })}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    {t('skills.history.use_version_btn')}
                  </Button>
                )}
              </div>
            </div>
            <p className="text-sm font-medium">{version.author}</p>
            {version.notes && (
              <p className="text-sm text-muted-foreground">{version.notes}</p>
            )}
            <button
              type="button"
              onClick={() => setExpandedVersion(expandedVersion === version.id ? null : version.id)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
            >
              {expandedVersion === version.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {expandedVersion === version.id ? t('skills.history.hide_prompt') : t('skills.history.show_prompt')}
            </button>
            {expandedVersion === version.id && (
              <pre className="whitespace-pre-wrap font-mono text-xs bg-muted/40 rounded-lg p-3 border max-h-60 overflow-y-auto mt-1">
                {version.prompt}
              </pre>
            )}
          </Card>
        ))}
      </div>

      {/* Context & resources */}
      {hasContext && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('skills.context_section')}
          </h3>
          <Card className="p-4 space-y-4">
            {coverImageData?.url && (
              <img src={coverImageData.url} alt="" className="w-full h-40 object-cover rounded-md" />
            )}
            {skill.useCaseText && (
              <p className="text-sm text-foreground">{skill.useCaseText}</p>
            )}
            {skill.contextLinks && skill.contextLinks.length > 0 && (
              <ul className="space-y-1.5">
                {skill.contextLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {/* Current prompt */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('skills.history.prompt_section')} (v{skill.currentVersion})
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyPrompt}>
              <Copy className="h-4 w-4 mr-2" />
              {t('skills.history.copy_btn')}
            </Button>
            {canRestore && (
              <Button
                size="sm"
                onClick={() => navigate(`/skills-agentes/${skill.id}/new-version`)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('skills.history.new_version_btn')}
              </Button>
            )}
          </div>
        </div>
        <Card className="p-4">
          <pre className="whitespace-pre-wrap font-mono text-sm text-foreground overflow-x-auto">
            {currentVersionData?.prompt ?? ''}
          </pre>
        </Card>
      </div>
    </div>
  );
}
